import { useAppTranslation } from "@/utils";
import { Button, Flex, Typography, message, notification, theme } from "@payping/web-components";
import { useEffect, useRef, useState } from "react";
import SignaturePad from "signature_pad";

interface UploadSignatureDrawProps {
  onFinish: (blob: File, data: string) => void;
  loading?: boolean;
  maxSize?: number;
}

const PayGetOrderUploadSignatureDraw = ({ onFinish, loading, maxSize }: UploadSignatureDrawProps) => {
  const { t } = useAppTranslation();

  const [canvasRef, setCanvasRef] = useState<HTMLCanvasElement | null>(null);
  const signaturePadRef = useRef<SignaturePad>(null);

  const { token } = theme.useToken();
  const [notif, contextHolder] = notification.useNotification({
    top: 16,
    duration: 5,
    maxCount: 1,
    placement: "top",
  });

  // eslint-disable-next-line react-hooks/immutability
  useEffect(() => {
    const signaturePad = signaturePadRef.current;
    const canvas = canvasRef;
    if (canvas) {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      // eslint-disable-next-line react-hooks/immutability
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext("2d")?.scale(ratio, ratio);
      (signaturePadRef as any).current = new SignaturePad(canvas, { backgroundColor: "#ffffff", penColor: "#000000" });
    }
    return () => {
      if (signaturePad) {
        signaturePad.clear();
        signaturePad.off();
      }
    };
  }, [canvasRef]);

  const clearDraw = () => {
    const signaturePad = signaturePadRef.current;
    if (signaturePad) {
      signaturePad.clear();
    }
  };

  async function confirmDraw() {
    if (loading) {
      return;
    }
    const signaturePad = signaturePadRef.current;

    if (signaturePad) {
      if (signaturePad.isEmpty()) {
        notif.error({
          message: String(t("pay.getOrder.steps.uploadSignature.form.errorForDraw")),
          closable: true,
        });
      }
      // continue if draw
      else {
        // Try original quality first
        let data = signaturePad.toDataURL("image/jpeg");
        let blob = await (await fetch(data)).blob();

        // If exceeds maxSize, try compression
        if (maxSize && blob.size >= maxSize) {
          // Try progressively lower quality until it fits, or we give up
          const qualities = [0.7, 0.5, 0.3];
          let compressed = false;

          for (let i = 0; i < qualities.length; i += 1) {
            data = signaturePad.toDataURL("image/jpeg", qualities[i]);
            // eslint-disable-next-line no-await-in-loop -- sequential by design; we stop at the first blob small enough
            blob = await (await fetch(data)).blob();

            if (blob.size < maxSize) {
              compressed = true;
              break;
            }
          }

          if (!compressed) {
            message.error({ content: t("pay.getOrder.steps.uploadSignature.sizeError") });
            return;
          }
        }

        onFinish(new File([blob], "signature.jpeg", { type: "image/jpeg" }), data);
      }
    }
  }

  return (
    <>
      {contextHolder}
      <Flex vertical gap={"small"}>
        <Typography.Title level={5} className="tw:m-0!">
          {t("pay.getOrder.steps.uploadSignature.form.sampleSignature")}
        </Typography.Title>
        <Typography.Text type="secondary">
          {t("pay.getOrder.steps.uploadSignature.form.sampleSignatureContent")}
        </Typography.Text>
        <canvas
          className="tw:h-[40dvh] tw:w-full tw:rounded-lg"
          style={{ border: "1px solid", borderColor: token.colorBorder }}
          ref={(ref) => setCanvasRef(ref)}
        ></canvas>
        <Button
          onClick={() => {
            clearDraw();
          }}
        >
          {t("pay.getOrder.steps.uploadSignature.form.redraw")}
        </Button>
        <Button
          loading={loading}
          className="tw:mt-auto"
          type="primary"
          onClick={() => {
            confirmDraw();
          }}
        >
          {t("pay.getOrder.steps.uploadSignature.form.signatureRegistration")}
        </Button>
      </Flex>
    </>
  );
};

export default PayGetOrderUploadSignatureDraw;
